import { compose, filter, find, includes, isEmpty, isNil, path, reject, sortBy } from 'ramda'
import {
  filterFields,
  getExternalBooleanDependentTxtFields,
  getExternalBusinessTypesFields,
  getExternalCheckboxFields,
  getExternalCommissionUnitsSelectFields,
  getExternalCountryFields,
  getExternalCurrencyFields,
  getExternalDateFields,
  getExternalDocsField,
  getExternalEcnAgreementFields,
  getExternalEmailFields,
  getExternalEmploymentDetailsField,
  getExternalEmploymentFields,
  getExternalEmploymentPositionFields,
  getExternalExternalEmployerNameField,
  getExternalFaceImageField,
  getExternalFeePeriodSelectFields,
  getExternalFundsFields,
  getExternalIbanCountryDependentFields,
  getExternalIbanFields,
  getExternalIdScanField,
  getExternalLastDateOfEmployment,
  getExternalLoginField,
  getExternalMaskedTextFields,
  getExternalNetworthCountryDependentFields,
  getExternalNetworthFields,
  getExternalPasswordFields,
  getExternalPhoneFields,
  getExternalPoliticallyExposedDetailsFields,
  getExternalStateFields,
  getExternalPriceTierField,
  getExternalPrivacyPolicyField,
  getExternalPurposeAccountCountryDependentFields,
  getExternalPurposeAccountDependentFields,
  getExternalRadioGroupCountryDependentFields,
  getExternalRadioGroupDependentFields,
  getExternalRadioGroupFields,
  getExternalSelectFields,
  getExternalSelectQuestionnaireFields,
  getExternalSelectResidenceDependentFields,
  getExternalSwiftCode,
  getExternalSwiftCodeCountryDependentFields,
  getExternalTextAreaFields,
  getExternalTextCountryDependentFields,
  getExternalTextFields,
  getExternalTransactionsFrequencyFields,
  getExternalTransactionsSizeFields,
  getExternalUnknownFields,
  getExternalWealthFields,
  getExternalPlatformSelectFields,
  getExternalHedgeTradingSelectFields
} from '../ExternalFields'
/**
 * @type {string[]}
 */
const FIELD_TYPES = [
  // input types
  'text',
  'email',
  'date',
  'tel',
  'masked',
  'employerName',
  'employmentDetails',
  'lastEmploymentDate',
  'passwordFields',
  'iban',
  'swiftCode',
  'login',
  'checkbox',
  'radioGroup',
  'radioGroupDependent',
  'privacyPolicy',
  'ecnAgreement',
  'purposeAccountDependentTxt',
  'booleanDependentTxt',
  'transactionsSizeRadio',
  'textarea',
  // select types
  'country',
  'currency',
  'employment',
  'funds',
  'wealth',
  'netWorth',
  'ctier',
  'businessTypeSelect',
  'positionIdSelect',
  'questionnaireSelect',
  'select',
  'annualIncomeId',
  'transactionsFrequency',
  'feePeriodSelect',
  'commissionUnitsSelect',
  'politicallyExposedDetails',
  'platformSelect',
  'hedgeTradingSelect',
  // custom types
  'flag',
  'docs',
  'faceImage',
  'idScan',
  'selectCountryDependent',
  'textCountryDependent',
  'radioGroupCountryDependent',
  'ibanCountryDependent',
  'swiftCodeCountryDependent',
  'purposeAccountCountryDependent',
  'networthCountryDependent',
  'state'
]

const filterUnknownFields = filter(({ type }) => !includes(type, FIELD_TYPES))

const getIsExtFldsValid = (validationObj, extFields) => {
  if (extFields?.length && (isNil(validationObj) || isEmpty(validationObj))) return false
  return Object.entries(validationObj || {}).reduce((acc, [name, value]) => {
    if (name === 'idBackSideScan') {
      name = 'idScan'
    }
    const fld = find(field => field.name === name)(extFields)
    if (!fld && name !== 'mobileVerification') return acc
    return (name !== 'mobileVerification' && fld.required) || name === 'mobileVerification'
      ? acc && value
      : acc
  }, true)
}

const sortExtFlds = compose(
  filter(fld => !isNil(fld)),
  sortBy(e => path(['props', 'order'])(e) ?? Number.MAX_VALUE)
)

/**
 *
 * @param {ExternalField[]} extFields
 * @returns {Object<string, ExternalField[]>}
 */
const getFieldsDescriptionByType = (extFields) => {
  const fldsObj = FIELD_TYPES.reduce((acc, fldType) => {
    acc[`${fldType}Fields`] = filterFields(fldType)(extFields)
    return acc
  }, {})
  fldsObj.unknownFields = filterUnknownFields(extFields)
  return fldsObj
}

/**
 * @param {ExternalField[]} fieldsDescription
 * @param {?boolean} showLabelAsPlaceholder
 * @returns {Object<string, Object[] | Object>}
 */
const getExternalFields = (fieldsDescription, showLabelAsPlaceholder = false) => {
  const fieldsDescriptionByType = getFieldsDescriptionByType(fieldsDescription)

  const fields = {
    externalTextFields: getExternalTextFields(fieldsDescriptionByType.textFields),
    externalStateFields: getExternalStateFields(fieldsDescriptionByType.stateFields),
    externalEmailFields: getExternalEmailFields(fieldsDescriptionByType.emailFields),
    externalMaskedFields: getExternalMaskedTextFields(fieldsDescriptionByType.maskedFields),
    externalDateFields: getExternalDateFields(fieldsDescriptionByType.dateFields),
    externalCountryFields: getExternalCountryFields(showLabelAsPlaceholder)(fieldsDescriptionByType.countryFields),
    externalPhoneFields: getExternalPhoneFields(fieldsDescriptionByType.telFields),
    externalCurrencyFields: getExternalCurrencyFields(fieldsDescriptionByType.currencyFields),
    externalCheckboxFields: getExternalCheckboxFields(showLabelAsPlaceholder)(fieldsDescriptionByType.checkboxFields),
    externalRadioGroupFields: getExternalRadioGroupFields(fieldsDescriptionByType.radioGroupFields),
    externalRadioGroupDependentFields: getExternalRadioGroupDependentFields(fieldsDescriptionByType.radioGroupDependentFields),
    externalPriceTierField: fieldsDescriptionByType.ctierFields.length
      ? getExternalPriceTierField(fieldsDescriptionByType.ctierFields[0])
      : null,
    externalFundsFields: getExternalFundsFields(fieldsDescriptionByType.fundsFields),
    externalWealthFields: getExternalWealthFields(fieldsDescriptionByType.wealthFields),
    externalNetworthFields: getExternalNetworthFields(fieldsDescriptionByType.netWorthFields),
    externalDocsField: fieldsDescriptionByType.docsFields.length
      ? getExternalDocsField(fieldsDescriptionByType.docsFields[0])
      : null,
    externalEmploymentFields: getExternalEmploymentFields(fieldsDescriptionByType.employmentFields),
    externalEmploymentDetailsField: fieldsDescriptionByType.employmentDetailsFields.length
      ? getExternalEmploymentDetailsField(fieldsDescriptionByType.employmentDetailsFields[0])
      : null,
    externalBusinessTypeField: fieldsDescriptionByType.businessTypeSelectFields.length
      ? getExternalBusinessTypesFields(fieldsDescriptionByType.businessTypeSelectFields[0])
      : null,
    externalEmploymentPositionField: fieldsDescriptionByType.positionIdSelectFields.length
      ? getExternalEmploymentPositionFields(fieldsDescriptionByType.positionIdSelectFields[0])
      : null,
    externalEmployerNameField: fieldsDescriptionByType.employerNameFields.length
      ? getExternalExternalEmployerNameField(fieldsDescriptionByType.employerNameFields[0])
      : null,
    externalLastEmploymentDateField: fieldsDescriptionByType.lastEmploymentDateFields.length
      ? getExternalLastDateOfEmployment(fieldsDescriptionByType.lastEmploymentDateFields[0])
      : null,
    swiftCodeField: fieldsDescriptionByType.swiftCodeFields.length
      ? getExternalSwiftCode(fieldsDescriptionByType.swiftCodeFields[0])
      : null,
    ibanField: fieldsDescriptionByType.ibanFields.length
      ? getExternalIbanFields(fieldsDescriptionByType.ibanFields[0])
      : null,
    passwordFields: fieldsDescriptionByType.passwordFieldsFields.length
      ? getExternalPasswordFields(fieldsDescriptionByType.passwordFieldsFields[0])
      : null,
    privacyPolicyField: fieldsDescriptionByType.privacyPolicyFields.length
      ? getExternalPrivacyPolicyField(fieldsDescriptionByType.privacyPolicyFields[0])
      : null,
    faceImageField: fieldsDescriptionByType.faceImageFields.length
      ? getExternalFaceImageField(fieldsDescriptionByType.faceImageFields[0])
      : null,
    idScanField: fieldsDescriptionByType.idScanFields.length
      ? getExternalIdScanField(fieldsDescriptionByType.idScanFields[0])
      : null,
    externalEcnAgreementField: fieldsDescriptionByType.ecnAgreementFields.length
      ? getExternalEcnAgreementFields(fieldsDescriptionByType.ecnAgreementFields[0])
      : null,
    loginField: fieldsDescriptionByType.loginFields.length
      ? getExternalLoginField(fieldsDescriptionByType.loginFields[0])
      : null,
    externalQuestionnaireSelectFields: getExternalSelectQuestionnaireFields(fieldsDescriptionByType.questionnaireSelectFields),
    externalSelectFields: getExternalSelectFields(fieldsDescriptionByType.selectFields),
    externalFeePeriodSelectFields: getExternalFeePeriodSelectFields(fieldsDescriptionByType.feePeriodSelectFields),
    externalPurposeAccountDependentFields: getExternalPurposeAccountDependentFields(fieldsDescriptionByType.purposeAccountDependentTxtFields),
    externalBooleanDependentTxtFields: getExternalBooleanDependentTxtFields(fieldsDescriptionByType.booleanDependentTxtFields),
    externalTransactionsFrequencyFields: getExternalTransactionsFrequencyFields(fieldsDescriptionByType.transactionsFrequencyFields),
    externalTransactionsSizeRadioField: fieldsDescriptionByType.transactionsSizeRadioFields.length
      ? getExternalTransactionsSizeFields(fieldsDescriptionByType.transactionsSizeRadioFields[0])
      : null,
    externalTextAreaFields: getExternalTextAreaFields(fieldsDescriptionByType.textareaFields),
    externalSelectResidenceDependentFields: getExternalSelectResidenceDependentFields(fieldsDescriptionByType.selectCountryDependentFields),
    externalTextCountryDependentFields: getExternalTextCountryDependentFields(fieldsDescriptionByType.textCountryDependentFields),
    externalRadioGroupCountryDependentFields: getExternalRadioGroupCountryDependentFields(fieldsDescriptionByType.radioGroupCountryDependentFields),
    externalIbanCountryDependentFields: fieldsDescriptionByType.ibanCountryDependentFields.length
      ? getExternalIbanCountryDependentFields(fieldsDescriptionByType.ibanCountryDependentFields[0])
      : null,
    externalSwiftCodeCountryDependentFields: fieldsDescriptionByType.swiftCodeCountryDependentFields.length
      ? getExternalSwiftCodeCountryDependentFields(fieldsDescriptionByType.swiftCodeCountryDependentFields[0])
      : null,
    externalPurposeAccountCountryDependentFields: getExternalPurposeAccountCountryDependentFields(fieldsDescriptionByType.purposeAccountCountryDependentFields),
    externalUnknownFields: getExternalUnknownFields(fieldsDescriptionByType.unknownFields),
    externalNetworthCountryDependentFields: getExternalNetworthCountryDependentFields(fieldsDescriptionByType.networthCountryDependentFields),
    externalCommissionUnitsSelectFields: getExternalCommissionUnitsSelectFields(fieldsDescriptionByType.commissionUnitsSelectFields),
    externalPoliticallyExposedDetailsFields: getExternalPoliticallyExposedDetailsFields(fieldsDescriptionByType.politicallyExposedDetailsFields),
    externalPlatformFields: getExternalPlatformSelectFields(fieldsDescriptionByType.platformSelectFields),
    externalHedgeTradingFields: getExternalHedgeTradingSelectFields(fieldsDescriptionByType.hedgeTradingSelectFields)
  }
  return reject(value => value === null || (Array.isArray(value) && !value.length), fields)
}

/**
 * @param {ExternalField[]} fieldsDescription
 * @returns {Object[]}
 */
const getExternalFieldsList = (fieldsDescription) => {
  const fldsObj = getExternalFields(fieldsDescription)
  let fldsList = []
  Object.keys(fldsObj).forEach((key) => {
    fldsList = fldsList.concat(fldsObj[key])
  })
  return fldsList
}

const removeQuestionnaireSteps = (extFields, allSteps, tier) => {
  const fldsDescrByTier = extFields.filter(({
    tier: fldTier,
    type
  }) => (fldTier === tier && type !== 'questionnaireSelect'))
  const descrBySteps = allSteps.map(step => fldsDescrByTier.filter(fld => fld.step === step.id))
  return allSteps.filter(step => !isEmpty(descrBySteps[step.id - 1]))
}

export {
  getIsExtFldsValid,
  sortExtFlds,
  getExternalFields,
  getExternalFieldsList,
  removeQuestionnaireSteps
}
